import { onScopeDispose, shallowRef, watch, type Ref, type ShallowRef } from 'vue';

/**
 * Whether the table's cells run on to the right of its scroller — it scrolls
 * sideways and is not at its end. The pinned row-action column then stands
 * over cells and shows an edge (I-2510). Measured on scroll and on size
 * changes of the scroller and its content, at most once per frame; the ref
 * only changes when the answer does.
 */
export function useContentRight(
  scroller: Readonly<Ref<HTMLElement | null | undefined>>
): Readonly<ShallowRef<boolean>> {
  const state = shallowRef(false);
  let frame = 0;
  let observer: ResizeObserver | null = null;
  let bound: HTMLElement | null = null;

  function measure(): void {
    frame = 0;
    const el = bound;
    if (!el) return;
    const next = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (state.value !== next) state.value = next;
  }

  function schedule(): void {
    if (frame || typeof requestAnimationFrame === 'undefined') return;
    frame = requestAnimationFrame(measure);
  }

  function unbind(): void {
    bound?.removeEventListener('scroll', schedule);
    observer?.disconnect();
    observer = null;
    bound = null;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  }

  watch(
    scroller,
    (el) => {
      unbind();
      if (!el) {
        state.value = false;
        return;
      }
      bound = el;
      el.addEventListener('scroll', schedule, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(schedule);
        observer.observe(el);
        for (const child of Array.from(el.children)) observer.observe(child);
      }
      schedule();
    },
    { immediate: true, flush: 'post' }
  );

  onScopeDispose(unbind);

  return state;
}
